import { Button } from '@/_components/ui/button'
import { downloadCsv } from '@/_helpers/csv'
import { removeSpecialCharactersAndSpaces } from '@/_helpers/remove-special-characters'
import { useFacilityMap } from '@/features/_hooks/use-facility-map'
import type { TStaffApplicationListItem } from '@/features/_lib/api/entities/applications.staff'
import { queries } from '@/features/_lib/react-query'
import type { TApplicationStatus } from '@doutorsim/shared/applications'
import type { TExposedFacilityDTO } from '@doutorsim/shared/exposed/facilities'
import { camelToSnakeCase } from '@doutorsim/shared/helpers/case'
import { centsToReais } from '@doutorsim/shared/helpers/currency'
import type { QueryClient } from '@tanstack/react-query'
import { useQueryClient } from '@tanstack/react-query'
import { Upload } from 'lucide-react'
import { useState } from 'react'
import { toast } from 'sonner'
import { getStatusConfig } from '../../_constants/admission-status'
import type { TUserType } from '../../_lib/auth-client.types'

type CellValue = string | number | boolean | null | undefined

type ExportableApplication = TStaffApplicationListItem & {
  facility: TExposedFacilityDTO | null
}

interface ColumnDef {
  label: string
  accessor: (app: ExportableApplication) => CellValue
}

function getExportColumns(userType: TUserType): ColumnDef[] {
  return [
    { label: 'id', accessor: (app) => app.id },
    { label: 'createdAt', accessor: (app) => new Date(app.createdAt).toISOString() },
    { label: 'Name', accessor: (app) => app.person.name },
    { label: 'Cpf', accessor: (app) => removeSpecialCharactersAndSpaces(app.person.cpf) },
    {
      label: 'Phone',
      accessor: (app) =>
        app.person.phone ? removeSpecialCharactersAndSpaces(app.person.phone) : '',
    },
    { label: 'Email', accessor: (app) => app.person.email },
    { label: 'facilityName', accessor: (app) => app.facility?.name ?? '' },
    { label: 'facilityCnpj', accessor: (app) => app.facility?.cnpj ?? '' },
    {
      label: 'status',
      accessor: (app) => getStatusConfig(app.status, userType)?.label ?? app.status,
    },
    {
      label: 'requestedAmount',
      accessor: (app) => (app.requestedAmount != null ? centsToReais(app.requestedAmount) : null),
    },
    {
      label: 'approvedAmount',
      accessor: (app) =>
        app.proposal?.disbursementAmount != null
          ? centsToReais(app.proposal.disbursementAmount)
          : null,
    },
    { label: 'installmentCount', accessor: (app) => app.installmentCount },
    { label: 'interestRate', accessor: (app) => app.proposal?.interestRate ?? null },
    {
      label: 'effectiveInterestRate',
      accessor: (app) => app.proposal?.effectiveInterestRate ?? null,
    },
    {
      label: 'iofAmount',
      accessor: (app) =>
        app.proposal?.iofAmount != null ? centsToReais(app.proposal.iofAmount) : null,
    },
    { label: 'registerFeeRate', accessor: (app) => app.proposal?.registerFeeRate ?? null },
    {
      label: 'totalAmountOwed',
      accessor: (app) =>
        app.proposal?.totalAmountOwed != null ? centsToReais(app.proposal.totalAmountOwed) : null,
    },
    { label: 'guarantorCount', accessor: (app) => app.guarantors.length },
  ]
}

interface ExportTableDataProps {
  mode: 'staff' | 'company' | 'facility'
  facilityId?: string
  companyId?: string
  userType: TUserType
  search?: string
  status?: TApplicationStatus[]
  startDate?: string
  endDate?: string
}

async function fetchApplications(
  queryClient: QueryClient,
  {
    mode,
    companyId,
    facilityId,
    search,
    status,
    startDate,
    endDate,
    facilityMap,
  }: {
    mode: 'staff' | 'company' | 'facility'
    companyId: string | undefined
    facilityId: string | undefined
    search?: string
    status?: TApplicationStatus[]
    startDate?: string
    endDate?: string
    facilityMap: Map<string, TExposedFacilityDTO> | null
  },
) {
  if (mode === 'staff') {
    const result = await queryClient.fetchQuery(
      queries.staffApplications.list({ search, status, limit: 100 }),
    )
    return (result?.data ?? []).map((app) => ({
      ...app,
      facility: facilityMap?.get(app.facilityId) ?? null,
    }))
  }

  if (mode === 'company') {
    const result = await queryClient.fetchQuery(
      queries.companyApplications.list(companyId ?? '', {
        search,
        status,
        startDate,
        endDate,
        limit: 100,
      }),
    )
    return (result?.data ?? []).map((app) => ({
      ...app,
      facility: facilityMap?.get(app.facilityId) ?? null,
    }))
  }

  const result = await queryClient.fetchQuery(
    queries.applications.list(companyId ?? '', facilityId ?? '', {
      search,
      status,
      startDate,
      endDate,
      limit: 50,
    }),
  )
  return result.data ?? []
}

export const ExportTableData = ({
  mode,
  facilityId,
  companyId,
  userType,
  search,
  status,
  startDate,
  endDate,
}: ExportTableDataProps) => {
  const facilityMap = useFacilityMap(mode, companyId)
  const queryClient = useQueryClient()
  const [isExporting, setIsExporting] = useState(false)

  const handleClick = async () => {
    setIsExporting(true)

    try {
      const rawApplications = await fetchApplications(queryClient, {
        mode,
        companyId,
        facilityId,
        search,
        status,
        startDate,
        endDate,
        facilityMap,
      })

      if (rawApplications.length === 0) {
        toast.error('Nenhum paciente encontrado.')
        return
      }

      const applications = rawApplications as ExportableApplication[]
      const columns = getExportColumns(userType)

      const maxGuarantors = Math.max(...applications.map((app) => app.guarantors.length), 0)
      const guarantorLabels = Array.from({ length: maxGuarantors }, (_, i) => `guarantor_${i + 1}`)

      const headers = [...columns.map((col) => camelToSnakeCase(col.label)), ...guarantorLabels]

      const rows = applications.map((app) => [
        ...columns.map((col) => col.accessor(app)),
        ...Array.from({ length: maxGuarantors }, (_, i) => app.guarantors[i]?.name),
      ])

      downloadCsv([headers, ...rows], 'Solicitações.csv')
      toast.success('Dados exportados com sucesso!')
    } catch {
      toast.error('Erro ao exportar dados.')
    } finally {
      setIsExporting(false)
    }
  }

  return (
    <Button
      variant="outline"
      size="sm"
      className="h-9 gap-2 font-medium"
      onClick={handleClick}
      disabled={isExporting}
    >
      <Upload size={16} />
      Exportar
    </Button>
  )
}
